import { faChartLine } from "@fortawesome/pro-regular-svg-icons/faChartLine";
import { faCog } from '@fortawesome/pro-regular-svg-icons/faCog';
import { faComments as faCommentsRegular } from '@fortawesome/pro-regular-svg-icons/faComments';
import { faDatabase } from "@fortawesome/pro-regular-svg-icons/faDatabase";
import { faGamepad as faGamepadRegular } from "@fortawesome/pro-regular-svg-icons/faGamepad";
import { faGraduationCap } from "@fortawesome/pro-regular-svg-icons/faGraduationCap";
import { faTachometerAlt } from "@fortawesome/pro-regular-svg-icons/faTachometerAlt";
import { faTrophy as faTrophyRegular } from "@fortawesome/pro-regular-svg-icons/faTrophy";
import { faComments } from '@fortawesome/pro-solid-svg-icons/faComments';
import { faDollarSign } from "@fortawesome/pro-solid-svg-icons/faDollarSign";
import { faEdit } from '@fortawesome/pro-solid-svg-icons/faEdit';
import { faGamepad } from "@fortawesome/pro-solid-svg-icons/faGamepad";
import { faHistory } from '@fortawesome/pro-solid-svg-icons/faHistory';
import { faHome } from '@fortawesome/pro-solid-svg-icons/faHome';
import { faMedal } from '@fortawesome/pro-solid-svg-icons/faMedal';
import { faNetworkWired } from "@fortawesome/pro-solid-svg-icons/faNetworkWired";
import { faNewspaper } from '@fortawesome/pro-solid-svg-icons/faNewspaper';
import { faPlusCircle } from '@fortawesome/pro-solid-svg-icons/faPlusCircle';
import { faQuestion } from '@fortawesome/pro-solid-svg-icons/faQuestion';
import { faRss } from '@fortawesome/pro-solid-svg-icons/faRss';
import { faTasks } from '@fortawesome/pro-solid-svg-icons/faTasks';
import { faThList } from '@fortawesome/pro-solid-svg-icons/faThList';
import { faTrophy } from "@fortawesome/pro-solid-svg-icons/faTrophy";
import { faUser } from "@fortawesome/pro-solid-svg-icons/faUser";
import { faUsers } from '@fortawesome/pro-solid-svg-icons/faUsers';
import { faFileSearch } from "@fortawesome/pro-solid-svg-icons/faFileSearch";
import * as coreApi from "api/Core";
import * as KnowledgeApi from "api/Knowledge";
import i18next from "i18next";
import * as FavoriteUtils from "lib/FavoriteUtils";
import LibUtils from "lib/LibUtils";
import { DynamicSystemRoles, ListType, SystemRoles } from "models/ApiEnums";
import { IDashboardMenu } from "models/AppModels";
import { Action } from "redux";
import { ThunkAction } from "redux-thunk";
import { RoutePaths } from "routes/RouteConfig";
import Session from "services/Session";
import { ApplicationState } from "store/rootReducer";
import { SidebarMessageKeys, SidebarMessageTypes } from "store/sidebar/windowMessages";
import { SideBarActionKeys } from "./keys";
import { ISideBarChangeNameAction, ISideBarUpdateAction, ISideBarUpdateGroupAction } from "./types";

//////////////////////////////////////////////////////

type ConfigResult = { options: IDashboardMenu[], loaders: Promise<void>[]; };

//////////////////////////////////////////////////////

function updateSideBar(data: IDashboardMenu[]): ISideBarUpdateAction {
    return { type: SideBarActionKeys.SIDEBAR_UPDATE_DATA, payload: data };
}

//////////////////////////////////////////////////////

function getSidebarMenu(): ConfigResult {

    const loaders: Promise<void>[] = [];

    const options: IDashboardMenu[] = [
        {
            navbarName: i18next.t("Common.Menu.Sidebar.Home.NavbarName"),
            navbarIcon: faCommentsRegular,
            navbarPath: RoutePaths.TimelinePrefix,
            prependSpacer: false,
            callout: null,
            groups: [
                {
                    group: null,
                    items: [
                        {
                            name: i18next.t("Common.Menu.Sidebar.Home.TimelineStartup"),
                            path: RoutePaths.TimelineStartup,
                            icon: faHome,
                            showMode: "regular",
                            callout: null
                        },
                        {
                            name: i18next.t("Common.Menu.Sidebar.Home.TimelineHighlights"),
                            path: RoutePaths.TimelineHighlights,
                            icon: faNewspaper,
                            showMode: "regular",
                            callout: null
                        },
                        {
                            name: i18next.t("Common.Menu.Sidebar.Home.TimelineFollowing"),
                            path: RoutePaths.TimelineFollowing,
                            icon: faRss,
                            showMode: "regular",
                            callout: null
                        },
                        {
                            name: i18next.t("Common.Menu.Sidebar.Home.TimelineQuestions"),
                            path: RoutePaths.TimelineQuestions,
                            icon: faQuestion,
                            showMode: "regular",
                            callout: null
                        },
                        {
                            name: i18next.t("Common.Menu.Sidebar.Home.TimelineCourses"),
                            path: RoutePaths.TimelineCourses,
                            icon: faGraduationCap,
                            showMode: "regular",
                            callout: null,
                            hideIfNoCanViewCourses: true,
                        },
                    ]
                },
                {
                    group: i18next.t("Common.Menu.Sidebar.Home.MyActions"),
                    items: [
                        {
                            name: i18next.t("Common.Menu.Sidebar.Home.TimelinePublications"),
                            path: RoutePaths.TimelinePublications,
                            icon: faEdit,
                            showMode: "regular",
                            callout: null
                        },
                        {
                            name: i18next.t("Common.Menu.Sidebar.Home.TimelineHistory"),
                            path: RoutePaths.TimelineHistory,
                            icon: faHistory,
                            showMode: "regular",
                            callout: null,
                        },
                        {
                            name: i18next.t("Common.Menu.Sidebar.Home.TimelineActions"),
                            path: RoutePaths.TimelineActions,
                            icon: faTasks,
                            showMode: "regular",
                            callout: null,
                            hideIfNoCallout: true,
                        }
                    ]
                },
                {
                    group: i18next.t("Common.Menu.Sidebar.Home.MyLists"),
                    loader: (dispatch: any, data: IDashboardMenu[], leftIndex: number, rightIndex: number) => loaders.push(loadFavorite(dispatch, data, leftIndex, rightIndex)),
                    loading: true,
                    listThreshold: 3,
                    listCurrentIndex: 0,
                    listOverflowName: i18next.t("Common.Menu.Sidebar.Literals.Lists"),
                    listOverflowNameSingular: i18next.t("Common.Menu.Sidebar.Literals.List"),
                    items: [],
                },
                {
                    group: i18next.t("Common.Menu.Sidebar.Home.TopicsFollowed"),
                    loader: (dispatch: any, data: IDashboardMenu[], leftIndex: number, rightIndex: number) => loaders.push(loadTopics(dispatch, data, leftIndex, rightIndex)),
                    loading: true,
                    listThreshold: 50,
                    listCurrentIndex: -1,
                    listUseDivider: true,
                    listOverflowName: i18next.t("Common.Menu.Sidebar.Literals.Topics"),
                    listOverflowNameSingular: i18next.t("Common.Menu.Sidebar.Literals.Topic"),
                    items: [],

                    groupButton: {
                        icon: faPlusCircle,
                        path: RoutePaths.TopicFollowList,
                        title: i18next.t("Common.Menu.Sidebar.Home.AddTopics")
                    }
                }
            ]
        },

        {
            navbarName: i18next.t("Common.Menu.Sidebar.QuizSelection.NavbarName"),
            navbarIcon: faGamepadRegular,
            navbarPath: RoutePaths.QuizSelection,
            prependSpacer: false,
            callout: null,
            groups: []
        },

        {
            navbarName: i18next.t("Common.Menu.Sidebar.UserEvolutionDashboard.NavbarName"),
            navbarIcon: faChartLine,
            navbarPath: RoutePaths.UserEvolutionDashboard,
            prependSpacer: true,
            callout: null,
            groups: []
        },
        {
            navbarName: i18next.t("Common.Menu.Sidebar.AwardsAndCreditsDashboard.NavbarName"),
            navbarIcon: faTrophyRegular,
            navbarPath: RoutePaths.AwardsAndCreditsDashboard,
            prependSpacer: false,
            callout: null,
            groups: []
        },

        //{
        //    navbarName: "Lightbulb",
        //    navbarIcon: faLightbulb,
        //    navbarPath: null,
        //    prependSpacer: false,
        //    callout: null,
        //    groups: []
        //},

        {
            navbarName: i18next.t("Common.Menu.Sidebar.Dashboards.NavbarName"),
            navbarIcon: faTachometerAlt,
            navbarPath: null,
            prependSpacer: true,
            callout: null,
            groups: [
                {
                    group: null,
                    items: [
                        {
                            name: i18next.t("Common.Menu.Sidebar.Dashboards.UserAndGroupsDashboard"),
                            path: RoutePaths.UserAndGroupsDashboard,
                            icon: faUser,
                            showMode: "regular",
                            callout: null,
                            roles: SystemRoles.Administrator | SystemRoles.UserManager | SystemRoles.PerformanceAnalyst,
                            grouping: 1
                        },

                        {
                            name: i18next.t("Common.Menu.Sidebar.Dashboards.UserAndGroupsTeamDashboard"),
                            path: RoutePaths.UserAndGroupsTeamDashboard,
                            icon: faNetworkWired,
                            showMode: "regular",
                            callout: null,
                            roles: null,
                            dynamicRoles: DynamicSystemRoles.DashboardManager,
                            grouping: 1
                        },

                        {
                            name: i18next.t("Common.Menu.Sidebar.Dashboards.PublicationAnalysisDashboard"),
                            path: RoutePaths.PublicationAnalysisDashboard,
                            icon: faComments,
                            showMode: "regular",
                            callout: null,
                            roles: SystemRoles.Administrator | SystemRoles.PerformanceAnalyst,
                            grouping: 2
                        },

                        {
                            name: i18next.t("Common.Menu.Sidebar.Dashboards.CourseAnalysisDashboard"),
                            path: RoutePaths.CourseAnalysisDashboard,
                            icon: faGraduationCap,
                            showMode: "regular",
                            callout: null,
                            roles: SystemRoles.Administrator | SystemRoles.CourseManager | SystemRoles.PerformanceAnalyst,
                            grouping: 2
                        },

                        {
                            name: i18next.t("Common.Menu.Sidebar.Dashboards.KnowledgeTestAnalysisDashboard"),
                            path: RoutePaths.KnowledgeTestAnalysisDashboard,
                            icon: faDatabase,
                            showMode: "regular",
                            callout: null,
                            roles: SystemRoles.Administrator | SystemRoles.TrainingManager,
                            grouping: 2
                        },

                        {
                            name: i18next.t("Common.Menu.Sidebar.Dashboards.KnowledgeTestDashboard"),
                            path: RoutePaths.KnowledgeTestDashboard,
                            icon: faGamepad,
                            showMode: "regular",
                            callout: null,
                            roles: SystemRoles.Administrator | SystemRoles.PerformanceAnalyst,
                            grouping: 3
                        },
                        {
                            name: i18next.t("Common.Menu.Sidebar.Dashboards.AwardAnalysisDashboard"),
                            path: RoutePaths.AwardAnalysisDashboard,
                            icon: faTrophy,
                            showMode: "regular",
                            callout: null,
                            roles: SystemRoles.Administrator | SystemRoles.PerformanceAnalyst,
                            grouping: 3
                        },

                        {
                            name: i18next.t("Common.Menu.Sidebar.Dashboards.ConsumptionAnalysisDashboard"),
                            path: RoutePaths.ConsumptionAnalysisDashboard,
                            icon: faDollarSign,
                            showMode: "regular",
                            callout: null,
                            roles: SystemRoles.Administrator,
                            grouping: 4
                        },
                    ]
                }
            ]
        },


        //{
        //    navbarName: "Dashboard",
        //    navbarIcon: faQuoteLeft,
        //    navbarPath: null,
        //    prependSpacer: true,
        //    callout: null,
        //    groups: []
        //},

        {
            navbarName: i18next.t("Common.Menu.Sidebar.Configuration.NavbarName"),
            navbarIcon: faCog,
            navbarPath: RoutePaths.ConfigPrefix,
            prependSpacer: true,
            callout: null,
            groups: [
                {
                    group: null,
                    items: [
                        {
                            name: i18next.t("Common.Menu.Sidebar.Configuration.UserAdminList"),
                            path: LibUtils.removeRouterOptionalParameters(RoutePaths.UserAdminList),
                            icon: faUser,
                            showMode: "regular",
                            callout: null,
                            roles: SystemRoles.Administrator | SystemRoles.UserManager,
                            grouping: 1
                        },

                        {
                            name: i18next.t("Common.Menu.Sidebar.Configuration.GroupAdminList"),
                            path: LibUtils.removeRouterOptionalParameters(RoutePaths.GroupAdminList),
                            icon: faUsers,
                            showMode: "regular",
                            callout: null,
                            roles: SystemRoles.Administrator | SystemRoles.UserManager,
                            grouping: 1
                        },

                        {
                            name: i18next.t("Common.Menu.Sidebar.Configuration.TopicAdminList"),
                            path: LibUtils.removeRouterOptionalParameters(RoutePaths.TopicAdminList),
                            icon: faThList,
                            showMode: "regular",
                            callout: null,
                            roles: SystemRoles.TopicManager | SystemRoles.TopicCreator,
                            dynamicRoles: DynamicSystemRoles.TopicAdminDelegation,
                            grouping: 2
                        },
                        {
                            name: i18next.t("Common.Menu.Sidebar.Configuration.PublicationAdminList"),
                            path: RoutePaths.PublicationAdminList,
                            icon: faComments,
                            showMode: "regular",
                            callout: null,
                            roles: SystemRoles.PublicationManager,
                            grouping: 2
                        },
                        {
                            name: i18next.t("Common.Menu.Sidebar.Configuration.CourseAdminList"),
                            path: RoutePaths.CourseAdminList,
                            icon: faGraduationCap,
                            showMode: "regular",
                            callout: null,
                            roles: SystemRoles.CourseManager | SystemRoles.CourseCreator,
                            grouping: 2
                        },
                        {
                            name: i18next.t("Common.Menu.Sidebar.Configuration.KnowledgeTestTopicList"),
                            path: RoutePaths.KnowledgeTestTopicList,
                            icon: faDatabase,
                            showMode: "regular",
                            callout: null,
                            roles: SystemRoles.TrainingManager | SystemRoles.TestCreator,
                            grouping: 2
                        },

                        {
                            name: i18next.t("Common.Menu.Sidebar.Configuration.GamificationSetup"),
                            path: RoutePaths.GamificationSetup,
                            icon: faMedal,
                            showMode: "regular",
                            callout: null,
                            roles: SystemRoles.GameficationManager,
                            grouping: 3
                        },

                        {
                            name: i18next.t("Common.Menu.Sidebar.Configuration.SystemLog"),
                            path: RoutePaths.SystemLog,
                            icon: faFileSearch,
                            showMode: "regular",
                            callout: null,
                            roles: SystemRoles.Administrator | SystemRoles.PublicationManager,
                            grouping: 4
                        },

                        {
                            name: i18next.t("Common.Menu.Sidebar.Configuration.SetupMain"),
                            path: RoutePaths.SetupMain,
                            icon: faCog,
                            showMode: "regular",
                            callout: null,
                            roles: SystemRoles.Administrator | SystemRoles.UserManager | SystemRoles.PublicationManager | SystemRoles.TrainingManager,
                            grouping: 4
                        }


                    ]
                }
            ]
        },

    ];

    return { options, loaders };
}

function buildUserMenu(menu: IDashboardMenu[]): IDashboardMenu[] {
    const userMenu: IDashboardMenu[] = [];

    for (var i = 0; i < menu.length; i++) {

        let m: IDashboardMenu = null;

        if (menu[i].roles) {
            if (Session.hasRole(menu[i].roles)) {
                m = menu[i];
            }
        }
        else {
            m = menu[i];
        }


        if (m) {
            if (m.groups && m.groups.length > 0) {

                const acceptGroup = true;

                for (let j = 0; j < m.groups.length && acceptGroup; j++) {

                    // verificar se há permissão explícita no grupo
                    let groupPermission = (m.groups[j].roles == null || Session.hasRole(m.groups[j].roles));

                    // verifica se há permissão nos items do grupo
                    if (groupPermission)
                        if (m.groups[j].items.length > 0) {

                            m.groups[j].items = m.groups[j].items.filter(x =>
                                (x.roles == null && x.dynamicRoles == null)
                                ||
                                (x.dynamicRoles == null && x.roles != null && Session.hasRole(x.roles))
                                ||
                                (x.roles == null && (x.dynamicRoles != null && Session.hasDynamicRole(x.dynamicRoles)))
                                ||
                                (x.dynamicRoles != null && x.roles != null && (Session.hasRole(x.roles) || Session.hasDynamicRole(x.dynamicRoles)))
                            );

                            groupPermission = m.groups[j].items.length > 0;
                        }

                    // remove grupo se não tiver permissão ou estiver sem nenhum item
                    if (!groupPermission) {
                        m.groups = m.groups.filter(x => x.group != m.groups[j].group);
                    }
                }

                if (m.groups.length > 0) {
                    // nenhum acesso ao grupo
                    userMenu.push(m);
                }
            }
            else
                userMenu.push(m);
        }
    }

    return userMenu;
}

function buildList(dispatch: any): ConfigResult {

    const menu = getSidebarMenu();

    menu.options = buildUserMenu(menu.options);

    for (var i = 0; i < menu.options.length; i++) {
        for (var j = 0; j < menu.options[i].groups.length; j++) {
            if (menu.options[i].groups[j].loader != null) {
                menu.options[i].groups[j].loader(dispatch, menu.options, i, j);
            }
        }
    }

    return menu;
}

function loadFavorite(dispatch: any, data: IDashboardMenu[], leftIndex: number, rightIndex: number): Promise<void> {
    return new Promise<void>((resolve) => {


        KnowledgeApi.getUserFavoriteList()
            .then((result) => {

                data[leftIndex].groups[rightIndex].items = [];
                data[leftIndex].groups[rightIndex].loaderError = null;
                data[leftIndex].groups[rightIndex].loading = false;

                result.forEach((v) => {

                    data[leftIndex].groups[rightIndex].items.push(
                        {
                            name: v.title,
                            icon: FavoriteUtils.getIcon(v.listType, v.isPublic),
                            path: RoutePaths.TimelineList.replace(":id", v.listId),
                            showMode: v.listType == ListType.Custom ? "temp" : "regular",
                            callout: null
                        }
                    );

                });

                dispatch(updateSideBar(data));

                resolve();
            })
            .catch((error) => {
                data[leftIndex].groups[rightIndex].loaderError = error;
                resolve();
            });
    });
}

function loadTopics(dispatch: any, data: IDashboardMenu[], leftIndex: number, rightIndex: number): Promise<void> {
    return new Promise<void>((resolve) => {


        coreApi.getUserSidebarFollowingTopics()
            .then((result) => {

                data[leftIndex].groups[rightIndex].items = [];
                data[leftIndex].groups[rightIndex].loaderError = null;
                data[leftIndex].groups[rightIndex].loading = false;

                result.forEach((v) => {

                    data[leftIndex].groups[rightIndex].items.push(
                        {
                            name: v.name,
                            icon: v.thumbnailUrl,
                            path: RoutePaths.TimelineTopic.replace(":id", v.id),
                            showMode: v.fixed ? "regular" : "temp",
                            callout: null
                        }
                    );

                });

                dispatch(updateSideBar(data));

                resolve();
            })
            .catch((error) => {
                data[leftIndex].groups[rightIndex].loaderError = error;
                resolve();
            });
    });
}

//////////////////////////////////////////////////////

export function changeName(path: string, newName: string): ISideBarChangeNameAction {
    return {
        type: SideBarActionKeys.SIDEBAR_CHANGE_NAME, payload: { path, newName }
    };
}

export function clear(): ISideBarUpdateAction {
    return {
        type: SideBarActionKeys.SIDEBAR_UPDATE_DATA,
        payload: []
    };
}

export function updateGroupListItems(group: string, newShowedItems: number): ISideBarUpdateGroupAction {
    return {
        type: SideBarActionKeys.SIDEBAR_UPDATE_GROUP_SHOWED_ITEMS, payload: { group, newShowedItems }
    };
}

export function load(): ThunkAction<void, ApplicationState, void, Action> {
    return (dispatch, getState) => {
        if (getState().sidebar.menuData.length == 0) {
            const newList = buildList(dispatch).options;
            dispatch(updateSideBar(newList));
        }
    };
}

export function reset(): ThunkAction<void, ApplicationState, void, Action> {
    return (dispatch) => {
        const newData = buildList(dispatch);
        Promise.all(newData.loaders).then(() => {
            dispatch(updateSideBar(newData.options));
        });
    };
}

export function executeSideBarMessage(messageType: SidebarMessageTypes): ThunkAction<void, ApplicationState, void, Action> {
    return (dispatch) => {

        if (!messageType)
            return;

        switch (messageType.type) {
            case SidebarMessageKeys.RESET:
                dispatch(reset());
                break;
        }
    };
}

//////////////////////////////////////////////////////